import React, { useState, useEffect, useRef } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import './ScanQRCode.css';
import { API_URL } from '../config/api';

const ScanQRCode = ({ phoneId, user }) => {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    return () => {
      // Nettoyer le scanner lors du démontage
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {});
      }
    };
  }, []);

  const startScan = async () => {
    if (!phoneId) {
      setError('Identifiant du téléphone non disponible');
      return;
    }

    try {
      setError(null);
      setResult(null);
      setScanning(true);

      const html5QrCode = new Html5Qrcode("qr-reader");
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          handleScan(decodedText);
        },
        (errorMessage) => {
          // Ignorer les erreurs de scan continu
        }
      );
    } catch (err) {
      console.error('Erreur lors du démarrage du scan:', err);
      setError('Impossible de démarrer le scanner. Vérifiez les permissions de la caméra.');
      setScanning(false);
    }
  };

  const stopScan = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        await html5QrCodeRef.current.clear();
      } catch (err) {
        console.error('Erreur lors de l\'arrêt du scan:', err);
      }
      html5QrCodeRef.current = null;
    }
    setScanning(false);
  };

  const handleScan = async (qrCode) => {
    // Arrêter le scanner après avoir détecté un QR code
    await stopScan();

    try {
      // Créer une demande (l'admin choisira l'élément)
      const scanResponse = await fetch(`${API_URL}/api/scans/scan`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          qr_code: qrCode,
          phone_id: phoneId
        }),
      });

      const scanData = await scanResponse.json();

      if (scanResponse.ok) {
        setResult({
          success: true,
          message: 'Demande créée avec succès! L\'administrateur va choisir l\'élément à vous attribuer.',
          request: scanData
        });
      } else {
        setResult({
          success: false,
          message: scanData.error || 'Erreur lors du scan',
          details: scanData
        });
      }
    } catch (err) {
      console.error('Erreur lors du traitement du scan:', err);
      setError('Erreur lors du traitement du scan');
    }
  };

  const handleManualInput = async () => {
    const qrCode = prompt('Entrez le code QR manuellement:');
    if (qrCode) {
      await handleScan(qrCode);
    }
  };

  return (
    <div className="scan-qrcode">
      <h1>Scanner le QR Code</h1>
      
      <div className="alert alert-info">
        <p><strong>📱 QR Code unique pour tous les éléments</strong></p>
        <p>Scannez le QR code général affiché. L'administrateur choisira ensuite l'élément à vous attribuer depuis sa liste.</p>
      </div>

      {!user && (
        <div className="alert alert-warning">
          <p>Vous devez être identifié pour scanner un QR code.</p>
          <p>L'identification se fait automatiquement lors de la première utilisation.</p>
        </div>
      )}

      <div className="card">
        <h2>Scanner</h2>
        
        {!scanning ? (
          <div className="scan-controls">
            <button className="btn btn-primary btn-large" onClick={startScan}>
              Démarrer le scan
            </button>
            <button className="btn btn-secondary btn-large" onClick={handleManualInput}>
              Saisie manuelle
            </button>
          </div>
        ) : (
          <div className="scanner-container">
            <div id="qr-reader" className="qr-reader"></div>
            <button className="btn btn-danger" onClick={stopScan}>
              Arrêter le scan
            </button>
          </div>
        )}

        {error && (
          <div className="alert alert-error">
            {error}
          </div>
        )}

        {result && (
          <div className={`alert alert-${result.success ? 'success' : 'error'}`}>
            <h3>{result.success ? '✓ Succès' : '✗ Erreur'}</h3>
            <p>{result.message}</p>
            
            {result.success && result.request && (
              <div className="request-info">
                <p><strong>Numéro de demande:</strong> #{result.request.request_id}</p>
                <p><strong>Statut:</strong> En attente de validation par l'administrateur</p>
                <p className="info-text">
                  Votre demande a été enregistrée. L'administrateur va choisir l'élément à vous attribuer.
                </p>
              </div>
            )}

            {result.details && result.details.next_available_date && (
              <div className="restriction-info">
                <p><strong>Dernier scan:</strong> {new Date(result.details.last_scan_date).toLocaleDateString('fr-FR')}</p>
                <p><strong>Prochaine disponibilité:</strong> {new Date(result.details.next_available_date).toLocaleDateString('fr-FR')}</p>
              </div>
            )}

            <button 
              className="btn btn-secondary" 
              onClick={() => {
                setResult(null);
                setError(null);
              }}
            >
              Nouveau scan
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ScanQRCode;

